import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import cookie from 'js-cookie';
import { get as getValueAtPath } from 'lodash';
import PasswordExpiredModal from './passwordExpiredModal';
import AddEmailAddressModal from './addEmailAddressModal';
import CUIPolicyModal from './cuiPolicyModal';
import SecretSetup from '../common/security/secretSetup';
import ga from '../../utils/ga';
import entityComposition from '../common/classes/entityComposition';
import LoginForm from './loginForm';
import PostAuthDefaultLanding from './postAuthDefaultLanding';
import { getLandingGateSnapshot, startLandingCheck, usePostAuthLandingGate } from './postAuthLandingGate';
import appHistory from '../../utils/appHistory';
const safeParseRememberMe = cookieValue => {
  try {
    return JSON.parse(cookieValue || 'false');
  } catch (error) {
    // If JSON parsing fails, default to false
    return false;
  }
};
const LoginFormContainer = _ref => {
  var _login$user2;
  let _ref$location = _ref.location,
    location = _ref$location === void 0 ? null : _ref$location,
    login = _ref.login,
    loginActions = _ref.loginActions,
    passwordModel = _ref.password,
    passwordActions = _ref.passwordActions,
    children = _ref.children,
    isMFAModalShown = _ref.isMFAModalShown;
  const routerLocation = useLocation();
  const landingGatePhase = usePostAuthLandingGate();
  const rememberMeFromCookie = safeParseRememberMe(cookie.get('rememberMe'));
  const isPostAuthLandingRoute = routerLocation.pathname === '/' || routerLocation.pathname === '/file-cabinet';
  const _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    customer = _useState2[0],
    setCustomer = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    username = _useState4[0],
    setUsername = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    password = _useState6[0],
    setPassword = _useState6[1];
  const _useState7 = useState(''),
    _useState8 = _slicedToArray(_useState7, 2),
    mfaPin = _useState8[0],
    setMfaPin = _useState8[1];
  const _useState9 = useState(rememberMeFromCookie),
    _useState0 = _slicedToArray(_useState9, 2),
    rememberMe = _useState0[0],
    setRememberMe = _useState0[1];
  useEffect(() => {
    if (rememberMe) {
      const cookieCustomer = cookie.get('customer') || '';
      const cookieUsername = cookie.get('username') || '';
      setCustomer(cookieCustomer);
      setUsername(cookieUsername);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (login.clearPassword) {
      setPassword('');
      setMfaPin('');
    }
  }, [login.clearPassword]);
  useLayoutEffect(() => {
    var _login$user;
    if (!login.isAuthenticated || login.shouldSetNewEmail || login.showCUIPolicy) {
      return;
    }
    const uid = (_login$user = login.user) === null || _login$user === void 0 ? void 0 : _login$user.user_id;
    if (!uid || !isPostAuthLandingRoute) {
      return;
    }
    if (getLandingGateSnapshot() === 'idle') {
      startLandingCheck();
    }
  }, [login.isAuthenticated, (_login$user2 = login.user) === null || _login$user2 === void 0 ? void 0 : _login$user2.user_id, login.shouldSetNewEmail, login.showCUIPolicy, isPostAuthLandingRoute]);
  useEffect(() => {
    if (!login.isAuthenticated) {
      ga.trackPage('Login Screen: View');
      ga.trackEvent('Login Screen', 'View');
    }
    if (login.isAuthenticated && location) {
      appHistory.replace(location);
    }
  }, [login.isAuthenticated, location]);

  // Handle customer blur - check SSO status when user leaves the field
  // Using blur instead of debounced input to avoid overwhelming the server with requests
  // This ensures we only check once when the user is done entering the customer name
  const handleCustomerBlur = () => {
    if (customer && customer.trim()) {
      loginActions.checkSSOConfig(customer.trim());
    } else {
      loginActions.clearSSOConfig();
    }
  };
  const handleChange = (key, incomingValue) => {
    let value = incomingValue;
    if (key === 'customer') {
      value = incomingValue.toLowerCase();
    }
    if (key === 'customer') {
      setCustomer(incomingValue);
      cookie.set(key, value);
    } else if (key === 'username') {
      setUsername(incomingValue);
      cookie.set(key, value);
    } else if (key === 'password') {
      setPassword(incomingValue);
    } else if (key === 'mfaPin') {
      setMfaPin(incomingValue);
    } else if (key === 'rememberMe') {
      setRememberMe(incomingValue);
      cookie.set(key, value);
    }
  };
  const handleLogin = () => {
    if (!customer || !username || !password) {
      loginActions.loginFail({
        error_msg: 'All fields are required'
      });
      return;
    }
    loginActions.serverLogin(customer, username, password, mfaPin);
  };

  /**
   * Save the new password in state in case the user also has to create an MFA pin
   */
  const handleNewPassword = newPassword => {
    const userID = getValueAtPath(login, 'user.user_id', '');
    passwordActions.serverSavePassword(userID, newPassword, customer, username, mfaPin);
    setPassword(newPassword);
  };

  /**
   * Save the new pin in state in case the user also has to update their password
   */
  const handleNewPin = newPin => setMfaPin(newPin);

  // Allow Entra callback route to render even when not authenticated
  // Check both the React Router pathname (hash route) and window pathname (actual URL path)
  // Also check the hash in case the code is in the hash fragment
  const routerPathname = (routerLocation === null || routerLocation === void 0 ? void 0 : routerLocation.pathname) || '';
  // eslint-disable-next-line no-undef
  const windowPathname = typeof window !== 'undefined' ? window.location.pathname : '';
  // eslint-disable-next-line no-undef
  const windowHash = typeof window !== 'undefined' ? window.location.hash : '';
  // Check if we're on the callback route by:
  // 1. React Router pathname (for hash routes like /#/auth/callback)
  // 2. Window pathname (for path-based routes like /auth/callback.html)
  // 3. Hash contains code parameter (indicates we're processing a callback)
  const isEntraCallbackRoute = routerPathname === '/auth/callback' || windowPathname === '/auth/callback.html' || windowHash && windowHash.includes('code='); // If there's a code in the hash, we're on the callback

  // Note: authCallback.html handles redirecting from path-based /auth/callback.html
  // to hash-based /#/auth/callback. No additional redirect logic needed here

  let retval = /*#__PURE__*/React.createElement(LoginForm, {
    login: login,
    onChange: handleChange,
    onLogin: handleLogin,
    customer: customer,
    username: username,
    password: password,
    mfaPin: mfaPin,
    rememberMe: rememberMe,
    handleForgotPassword: loginActions.serverForgotPassword,
    forgotPasswordToggle: loginActions.forgotPasswordToggle,
    ssoProviders: login.ssoProviders || {},
    availableSSOProviders: login.availableSSOProviders || [],
    onCustomerBlur: handleCustomerBlur
  });

  // Show routes if authenticated
  // Note: EntraCallback route should NOT render routes until authentication completes
  // to prevent components from making API calls before auth finishes
  if (login.isAuthenticated) {
    if (login.shouldSetNewEmail) {
      retval = /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement(AddEmailAddressModal, {
        userID: login.user.user_id
      }));
    } else if (login.showCUIPolicy) {
      retval = /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement(CUIPolicyModal, null));
    } else {
      const deferMainContent = isPostAuthLandingRoute && landingGatePhase === 'checking';
      // show the main display
      retval = /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement(PostAuthDefaultLanding, null), deferMainContent ? null : children);
    }
  } else if (isEntraCallbackRoute) {
    // On Entra callback route but not authenticated yet - render children so
    // EntraCallback can render via React Router
    // React Router will match the /auth/callback route and render EntraCallback
    // EntraCallback will handle its own rendering and redirect after auth completes
    // Note: Header/Footer are conditionally rendered in app.jsx based on route,
    // so they won't mount during Entra callback, preventing premature API calls
    retval = /*#__PURE__*/React.createElement("section", null, children);
  } else if (login.passwordExpired) {
    // additional login steps
    retval = /*#__PURE__*/React.createElement("section", null, /*#__PURE__*/React.createElement(PasswordExpiredModal, {
      onSave: handleNewPassword,
      rules: passwordModel.rules
    }));
  } else if (login.mfaSetup && isMFAModalShown) {
    const loggedInCustomer = getValueAtPath(login, 'user.customer', '');
    const loggedInUsername = getValueAtPath(login, 'user.username', '');
    const loggedInUserID = getValueAtPath(login, 'user.user_id', '');
    retval = /*#__PURE__*/React.createElement(SecretSetup, {
      customer: loggedInCustomer,
      onSuccess: handleNewPin,
      username: loggedInUsername,
      userID: loggedInUserID,
      onlyModal: true
    });
  }
  return /*#__PURE__*/React.createElement("span", null, retval);
};
LoginFormContainer.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.string,
  isMFAModalShown: PropTypes.bool.isRequired,
  login: PropTypes.shape({
    showCUIPolicy: PropTypes.bool,
    clearPassword: PropTypes.bool,
    customer: PropTypes.string,
    mfaSetup: PropTypes.bool,
    isAuthenticated: PropTypes.bool,
    passwordExpired: PropTypes.bool,
    shouldSetNewEmail: PropTypes.bool,
    showForgotPassword: PropTypes.bool,
    user: PropTypes.shape({
      user_id: PropTypes.string
    }),
    ssoProviders: PropTypes.objectOf(PropTypes.shape({
      enabled: PropTypes.bool,
      loginUrl: PropTypes.string,
      checking: PropTypes.bool,
      settings: PropTypes.shape({
        tenantId: PropTypes.string,
        clientId: PropTypes.string,
        authority: PropTypes.string,
        redirectUri: PropTypes.string
      })
    })),
    availableSSOProviders: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  loginActions: PropTypes.shape({
    forgotPasswordToggle: PropTypes.func,
    loginFail: PropTypes.func,
    serverLogin: PropTypes.func,
    serverForgotPassword: PropTypes.func,
    checkSSOConfig: PropTypes.func,
    clearSSOConfig: PropTypes.func
  }).isRequired,
  password: PropTypes.shape({
    rules: PropTypes.shape({
      length: PropTypes.bool,
      upper: PropTypes.bool,
      lower: PropTypes.bool,
      symbol: PropTypes.bool
    })
  }).isRequired,
  passwordActions: PropTypes.shape({
    serverSavePassword: PropTypes.func
  }).isRequired
};
export { LoginFormContainer as TestLoginFormContainer };
export default entityComposition(LoginFormContainer, ['login', 'password'], {
  isMFAModalShown: state => state.mfa.modals.mfa
});